import React, { useState } from 'react';
import './Assessment.css';

/* ══════════════════════════════════════════════════════════
   DATA
══════════════════════════════════════════════════════════ */
const QUESTIONS = [
  { id: 1, text: "How clearly defined are the business outcomes expected from your AI initiatives?", options: [
    { text: "No clearly defined outcomes", score: 2 },
    { text: "Outcomes discussed informally", score: 5 },
    { text: "Outcomes defined for selected initiatives", score: 8 },
    { text: "Outcomes tied directly to business strategy", score: 10 },
  ]},
  { id: 2, text: "How mature is your organization's data infrastructure for supporting AI initiatives?", options: [
    { text: "Highly fragmented systems", score: 2 },
    { text: "Partial integration exists", score: 5 },
    { text: "Mostly integrated systems", score: 8 },
    { text: "Centralized AI-ready data architecture", score: 10 },
  ]},
  { id: 3, text: "How aligned are leadership teams on AI adoption priorities?", options: [
    { text: "No executive alignment", score: 2 },
    { text: "Occasional AI discussions", score: 5 },
    { text: "Alignment exists for selected initiatives", score: 8 },
    { text: "AI integrated into enterprise strategy", score: 10 },
  ]},
  { id: 4, text: "How prepared is your workforce to adopt AI-driven ways of working?", options: [
    { text: "Significant resistance to change", score: 2 },
    { text: "Limited awareness and adoption", score: 5 },
    { text: "Moderate adoption across teams", score: 8 },
    { text: "AI adoption embedded into culture", score: 10 },
  ]},
  { id: 5, text: "How effectively are AI initiatives prioritized and governed across the organization?", options: [
    { text: "No formal governance exists", score: 2 },
    { text: "Governance exists informally", score: 5 },
    { text: "Governance defined for major initiatives", score: 8 },
    { text: "Enterprise-wide AI governance framework established", score: 10 },
  ]},
  { id: 6, text: "How scalable are your current AI and automation initiatives?", options: [
    { text: "No AI initiatives", score: 2 },
    { text: "Pilot-stage experiments only", score: 5 },
    { text: "Limited scalability across functions", score: 8 },
    { text: "Scalable within selected business units", score: 10 },
  ]},
  { id: 7, text: "How effectively does your organization measure AI initiative performance and business impact?", options: [
    { text: "No measurable KPIs defined", score: 2 },
    { text: "Limited performance tracking", score: 5 },
    { text: "KPIs tracked for selected initiatives", score: 8 },
    { text: "Enterprise-wide AI value measurement framework", score: 10 },
  ]},
  { id: 8, text: "How integrated are AI capabilities into your existing business processes?", options: [
    { text: "AI is not integrated into operations", score: 2 },
    { text: "Isolated AI use cases exist", score: 5 },
    { text: "AI integrated into selected workflows", score: 8 },
    { text: "AI embedded into core operational processes", score: 10 },
  ]},
  { id: 9, text: "How mature are your cybersecurity and risk management capabilities for AI initiatives?", options: [
    { text: "No AI-specific risk framework exists", score: 2 },
    { text: "Basic security controls in place", score: 5 },
    { text: "Risk management defined for key initiatives", score: 8 },
    { text: "Enterprise-grade AI governance and risk controls", score: 10 },
  ]},
  { id: 10, text: "How prepared is your organization to scale AI transformation over the next 12 months?", options: [
    { text: "No structured transformation roadmap", score: 2 },
    { text: "Early-stage planning underway", score: 5 },
    { text: "Roadmap defined for strategic initiatives", score: 8 },
    { text: "Organization is fully positioned for scaled transformation", score: 10 },
  ]},
];

const QUALIFICATION_OPTIONS = {
  priority:     ["AI Strategy & Roadmap", "Process Automation", "Data & Analytics", "Customer Experience Enhancement", "Enterprise AI Transformation"],
  timeline:     ["Long-term (After 12 months)", "Mid-term (6-12 months)", "Near-term (3-6 months)", "Immediate (0-3 months)"],
  investment:   ["Below $50K", "$50K-$100K", "$100K-$500K", "$500K+"],
  consultation: [
    "Yes - We are actively exploring transformation initiatives",
    "Exploring - We would like expert recommendations and next steps",
    "Evaluating - We are currently evaluating priorities internally",
    "Early Assessment - We are only assessing current maturity",
  ],
};

const RESULT_DATA = {
  Foundational: {
    label: "Foundational",
    range: "0 – 25",
    color: "foundational",
    desc: "Your organization is in the early stages of AI readiness and may require foundational alignment across strategy, governance, operational execution, and data capabilities.",
    actions: ["Define AI business outcomes clearly", "Assess data infrastructure gaps", "Build executive alignment on AI priorities"],
  },
  Emerging: {
    label: "Emerging",
    range: "26 – 50",
    color: "emerging",
    desc: "Your organization demonstrates growing AI maturity with opportunities to improve scalability, governance consistency, and enterprise operational alignment.",
    actions: ["Strengthen governance frameworks", "Improve cross-functional AI adoption", "Scale pilots to production"],
  },
  Advancing: {
    label: "Advancing",
    range: "51 – 75",
    color: "advancing",
    desc: "Your organization demonstrates strong strategic intent with growing operational readiness for scalable AI adoption and enterprise transformation.",
    actions: ["Accelerate enterprise-wide rollout", "Embed AI into core business processes", "Establish measurement frameworks"],
  },
  "Transformation Ready": {
    label: "Transformation Ready",
    range: "76 – 100",
    color: "ready",
    desc: "Your organization demonstrates advanced AI readiness, with strong alignment across strategy, operations, governance, and enterprise execution.",
    actions: ["Drive continuous AI innovation", "Explore next-gen AI capabilities", "Lead industry transformation"],
  },
};

/* ══════════════════════════════════════════════════════════
   ICONS
══════════════════════════════════════════════════════════ */
const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const SpinIcon = () => (
  <svg className="ar-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
  </svg>
);

/* ══════════════════════════════════════════════════════════
   STEP INDICATOR
══════════════════════════════════════════════════════════ */
const STEPS = ["Your Details", "Assessment", "Qualification", "Results"];

function StepBar({ current }) {
  return (
    <div className="ar-stepbar">
      {STEPS.map((label, i) => {
        const idx   = i + 1;
        const done  = idx < current;
        const active = idx === current;
        return (
          <React.Fragment key={i}>
            <div className={`ar-step ${active ? "ar-step--active" : ""} ${done ? "ar-step--done" : ""}`}>
              <div className="ar-step__dot">
                {done ? <CheckIcon /> : <span>{idx}</span>}
              </div>
              <span className="ar-step__label">{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`ar-step__line ${done ? "ar-step__line--done" : ""}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
function AIReadiness() {
  const [step,          setStep]          = useState(1);
  const [answers,       setAnswers]       = useState({});
  const [qIndex,        setQIndex]        = useState(0);
  const [qualification, setQualification] = useState({});
  const [submitted,     setSubmitted]     = useState(false);
  const [result,        setResult]        = useState(null);
  const [submitting,    setSubmitting]    = useState(false);
  const [errors,        setErrors]        = useState({});
  const [userInfo, setUserInfo] = useState({
    fullName: '', companyName: '', role: '',
    industry: '', organizationSize: '', email: '', phoneNumber: '',
  });

  /* ── Helpers ── */
  const getCategory = (score) => {
    if (score <= 25) return 'Foundational';
    if (score <= 50) return 'Emerging';
    if (score <= 75) return 'Advancing';
    return 'Transformation Ready';
  };

  const totalScore = () =>
    Object.values(answers).reduce((sum, s) => sum + s, 0);

  const scorePercent = () => Math.round((totalScore() / 100) * 100);

  /* ── Handlers ── */
  const handleUserInfo = (field, value) => {
    setUserInfo({ ...userInfo, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  const validateStep1 = () => {
    const req = { fullName: 'Full name', companyName: 'Company name', role: 'Role', email: 'Email', phoneNumber: 'Phone number' };
    const newErrors = {};
    Object.entries(req).forEach(([k, label]) => {
      if (!userInfo[k]?.trim()) newErrors[k] = `${label} is required`;
    });
    const emailRx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (userInfo.email && !emailRx.test(userInfo.email)) newErrors.email = 'Enter a valid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAnswer = (questionId, score) => {
    setAnswers({ ...answers, [questionId]: score });
  };

  const handleNext = () => {
    if (qIndex < QUESTIONS.length - 1) {
      setQIndex(qIndex + 1);
    } else {
      setStep(3);
    }
  };

  const handleQualification = (field, value) => {
    setQualification({ ...qualification, [field]: value });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const score    = totalScore();
    const category = getCategory(score);

    const payload = {
      assessmentType: 'ai-readiness',
      ...userInfo,
      answers: Object.entries(answers).map(([q, score]) => ({
        questionNumber: parseInt(q),
        answer: QUESTIONS.find(qq => qq.id === parseInt(q))?.options.find(o => o.score === score)?.text || '',
        score,
      })),
      qualification: {
        priority:            qualification.priority || '',
        timeline:            qualification.timeline || '',
        investment:          qualification.investment || '',
        consultationInterest: qualification.consultation || '',
        additionalInfo:      qualification.additionalInfo || '',
      },
      totalScore: score,
      resultCategory: category,
    };

    try {
      const res  = await fetch(`${import.meta.env.VITE_API_URL}/api/assessment/submit`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setResult({ totalScore: score, resultCategory: category });
        setSubmitted(true);
        setStep(4);
      } else {
        alert('Error: ' + (data.message || 'Submission failed'));
      }
    } catch (err) {
      console.error(err);
      alert('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentQ = QUESTIONS[qIndex];

  /* ══════════════════════════════════════════════════════
     STEP 4 — RESULT
  ══════════════════════════════════════════════════════ */
  if (step === 4 && result) {
    const rd = RESULT_DATA[result.resultCategory];
    return (
      <div className="ar-page">
        <div className="ar-hero ar-hero--result">
          <div className="ar-hero__inner">
            <p className="ar-eyebrow">AI Readiness Assessment</p>
            <h1 className="ar-hero__title">Your Results</h1>
          </div>
        </div>

        <div className="ar-body">
          <div className={`ar-result ar-result--${rd.color}`}>

            <div className="ar-result__top">
              <div className="ar-score-ring">
                <svg viewBox="0 0 120 120" className="ar-ring-svg">
                  <circle cx="60" cy="60" r="50" className="ar-ring-bg" />
                  <circle cx="60" cy="60" r="50" className="ar-ring-fill"
                    style={{ strokeDashoffset: `${314 - (314 * scorePercent()) / 100}` }} />
                </svg>
                <div className="ar-ring-label">
                  <span className="ar-ring-score">{result.totalScore}</span>
                  <span className="ar-ring-max">/100</span>
                </div>
              </div>

              <div className="ar-result__meta">
                <span className={`ar-badge ar-badge--${rd.color}`}>{rd.label}</span>
                <p className="ar-result__range">Score range: {rd.range}</p>
                <p className="ar-result__desc">{rd.desc}</p>
              </div>
            </div>

            <div className="ar-result__actions-block">
              <h3 className="ar-result__actions-title">Recommended Next Steps</h3>
              <ul className="ar-result__actions-list">
                {rd.actions.map((a, i) => (
                  <li key={i} className="ar-result__action">
                    <span className="ar-result__action-dot"><CheckIcon /></span>
                    {a}
                  </li>
                ))}
              </ul>
            </div>

            <div className="ar-result__cta">
              <a href="/contact" className="ar-btn ar-btn--gold">
                Talk to a BaiRees Advisor <ArrowRight />
              </a>
              <button onClick={() => window.location.reload()} className="ar-btn ar-btn--ghost">
                Retake Assessment
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     SHARED WRAPPER
  ══════════════════════════════════════════════════════ */
  return (
    <div className="ar-page">

      <div className="ar-hero">
        <div className="ar-hero__inner">
          <p className="ar-eyebrow">BaiRees Global Advisory</p>
          <h1 className="ar-hero__title">AI Readiness Assessment</h1>
          <p className="ar-hero__sub">
            Evaluate your organization's strategic, operational, governance, and technology
            readiness for scalable AI adoption.
          </p>
        </div>
      </div>

      <div className="ar-stepbar-wrap">
        <div className="ar-stepbar-inner">
          <StepBar current={step} />
        </div>
      </div>

      <div className="ar-body">

        {step === 1 && (
          <div className="ar-card">
            <div className="ar-card__header">
              <h2 className="ar-card__title">Participant Information</h2>
              <p className="ar-card__sub">Tell us about yourself so we can personalise your results.</p>
            </div>

            <div className="ar-form-grid">
              {[
                { field: 'fullName',         label: 'Full Name',          type: 'text',  placeholder: 'Your full name' },
                { field: 'companyName',      label: 'Company Name',       type: 'text',  placeholder: 'Your company' },
                { field: 'email',            label: 'Business Email',     type: 'email', placeholder: 'you@company.com' },
                { field: 'phoneNumber',      label: 'Phone Number',       type: 'tel',   placeholder: '+1 (555) 000-0000' },
              ].map(({ field, label, type, placeholder }) => (
                <div key={field} className="ar-field">
                  <label className="ar-field__label">{label} <span className="ar-req">*</span></label>
                  <input
                    className={`ar-field__input${errors[field] ? ' ar-field__input--error' : ''}`}
                    type={type}
                    value={userInfo[field]}
                    onChange={(e) => handleUserInfo(field, e.target.value)}
                    placeholder={placeholder}
                  />
                  {errors[field] && <span className="ar-field__error">{errors[field]}</span>}
                </div>
              ))}

              {[
                { field: 'role', label: 'Role', opts: ['Founder/CEO', 'C-Suite Executive (CXO)', 'Technology & AI Leader', 'Operations & Transformation Leader', 'Business Unit / P&L Head'] },
                { field: 'industry', label: 'Industry', opts: ['Technology', 'Healthcare', 'Manufacturing', 'Retail', 'Finance', 'Education', 'Logistics', 'Pharma', 'Consulting', 'Other'] },
                { field: 'organizationSize', label: 'Organization Size', opts: ['1-50 Employees', '50-100 Employees', '100-500 Employees', '500-1000 Employees', '1000+ Employees'] },
              ].map(({ field, label, opts }) => (
                <div key={field} className="ar-field">
                  <label className="ar-field__label">{label} <span className="ar-req">*</span></label>
                  <select
                    className={`ar-field__input ar-field__select${errors[field] ? ' ar-field__input--error' : ''}`}
                    value={userInfo[field]}
                    onChange={(e) => handleUserInfo(field, e.target.value)}
                  >
                    <option value="">Select {label.toLowerCase()}</option>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {errors[field] && <span className="ar-field__error">{errors[field]}</span>}
                </div>
              ))}
            </div>

            <div className="ar-card__footer">
              <button className="ar-btn ar-btn--primary" onClick={() => {
                if (validateStep1()) setStep(2);
              }}>
                Start Assessment <ArrowRight />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="ar-card">

            <div className="ar-progress">
              <div className="ar-progress__bar" style={{ width: `${(qIndex / QUESTIONS.length) * 100}%` }} />
            </div>
            <div className="ar-progress__meta">
              <span className="ar-progress__label">Question {qIndex + 1} of {QUESTIONS.length}</span>
              <span className="ar-progress__pct">{Math.round((qIndex / QUESTIONS.length) * 100)}% complete</span>
            </div>

            <div className="ar-question">
              <p className="ar-question__num">Q{qIndex + 1}</p>
              <h2 className="ar-question__text">{currentQ.text}</h2>
            </div>

            <div className="ar-options">
              {currentQ.options.map((opt, idx) => (
                <label
                  key={idx}
                  className={`ar-option${answers[currentQ.id] === opt.score ? ' ar-option--selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={`q${currentQ.id}`}
                    value={opt.score}
                    checked={answers[currentQ.id] === opt.score}
                    onChange={() => handleAnswer(currentQ.id, opt.score)}
                    className="ar-option__radio"
                  />
                  <span className="ar-option__marker">
                    {answers[currentQ.id] === opt.score ? <CheckIcon /> : <span>{idx + 1}</span>}
                  </span>
                  <span className="ar-option__text">{opt.text}</span>
                </label>
              ))}
            </div>

            <div className="ar-card__footer ar-card__footer--between">
              {qIndex > 0 && (
                <button className="ar-btn ar-btn--ghost" onClick={() => setQIndex(qIndex - 1)}>
                  ← Back
                </button>
              )}
              <button
                className="ar-btn ar-btn--primary"
                onClick={handleNext}
                disabled={!answers[currentQ.id]}
                style={{ marginLeft: qIndex === 0 ? 'auto' : undefined }}
              >
                {qIndex < QUESTIONS.length - 1 ? <>Next Question <ArrowRight /></> : <>Continue <ArrowRight /></>}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="ar-card">
            <div className="ar-card__header">
              <h2 className="ar-card__title">Qualification Questions</h2>
              <p className="ar-card__sub">Help us tailor our recommendations to your specific context.</p>
            </div>

            <div className="ar-qual-grid">
              {[
                { field: 'priority',     label: 'Which AI priority is most important for your organization?', opts: QUALIFICATION_OPTIONS.priority },
                { field: 'timeline',     label: 'What is your expected timeline for AI or transformation initiatives?', opts: QUALIFICATION_OPTIONS.timeline },
                { field: 'investment',   label: 'What level of investment is your organization considering?', opts: QUALIFICATION_OPTIONS.investment },
                { field: 'consultation', label: 'Would you like a strategic consultation based on your results?', opts: QUALIFICATION_OPTIONS.consultation },
              ].map(({ field, label, opts }) => (
                <div key={field} className="ar-field">
                  <label className="ar-field__label">{label}</label>
                  <select
                    className="ar-field__input ar-field__select"
                    value={qualification[field] || ''}
                    onChange={(e) => handleQualification(field, e.target.value)}
                  >
                    <option value="">Select an option</option>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              ))}

              <div className="ar-field ar-field--full">
                <label className="ar-field__label">Any additional information you would like to share?</label>
                <textarea
                  className="ar-field__input ar-field__textarea"
                  rows={4}
                  placeholder="Optional — share any context that would help us provide better recommendations…"
                  value={qualification.additionalInfo || ''}
                  onChange={(e) => handleQualification('additionalInfo', e.target.value)}
                />
              </div>
            </div>

            <div className="ar-card__footer ar-card__footer--between">
              <button className="ar-btn ar-btn--ghost" onClick={() => setStep(2)}>
                ← Back
              </button>
              <button className="ar-btn ar-btn--primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <><SpinIcon /> Submitting…</> : <>Submit Assessment <ArrowRight /></>}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default AIReadiness;